import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import RiskGauge from "@/components/RiskGauge";
import { VerdictBadge, SeverityBadge } from "@/components/RiskBadge";
import {
  ShieldAlert, Loader2, CheckCircle2, XCircle, Mic, Video, Download, Terminal, RefreshCw, ArrowLeft, Monitor, Trash2,
} from "lucide-react";

function detectOS() {
  const ua = navigator.userAgent;
  if (/Windows/i.test(ua)) return "Windows";
  if (/Mac OS X|Macintosh/i.test(ua)) return "Darwin";
  if (/Linux/i.test(ua)) return "Linux";
  return "Unknown";
}

function detectBrowser() {
  const ua = navigator.userAgent;
  if (/Edg\//.test(ua)) return "Microsoft Edge";
  if (/Chrome\//.test(ua)) return "Google Chrome";
  if (/Firefox\//.test(ua)) return "Firefox";
  if (/Safari\//.test(ua)) return "Safari";
  return "Browser";
}

export default function CandidateScan() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading"); // loading | invalid | expired | pending | completed
  const [info, setInfo] = useState(null);
  const [result, setResult] = useState(null);
  const [sig, setSig] = useState(null);
  const [devices, setDevices] = useState(null); // {audio:[], camera:[], flagged:[], denied}
  const [checking, setChecking] = useState(false);
  const os = detectOS();
  const pollRef = useRef(null);

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get(`/scan/status`, { params: { token } });
      if (data.status === "completed") {
        setResult(data);
        setStatus("completed");
        clearInterval(pollRef.current);
      } else {
        setStatus(data.status);
        if (data.status === "pending") setInfo(data);
      }
    } catch {
      /* keep polling */
    }
  }, [token]);

  useEffect(() => {
    poll();
    api.get("/scan/signatures").then(({ data }) => setSig(data)).catch(() => {});
    pollRef.current = setInterval(poll, 3000);
    return () => clearInterval(pollRef.current);
  }, [poll]);

  const runDeviceCheck = async () => {
    setChecking(true);
    let denied = false;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
      stream.getTracks().forEach((t) => t.stop());
    } catch {
      denied = true;
    }
    let list = [];
    try {
      list = await navigator.mediaDevices.enumerateDevices();
    } catch {
      /* unsupported */
    }
    const audio = [...new Set(list.filter((d) => d.kind !== "videoinput").map((d) => d.label).filter(Boolean))];
    const camera = [...new Set(list.filter((d) => d.kind === "videoinput").map((d) => d.label).filter(Boolean))];

    const flagged = [];
    const flaggedAudio = [];
    const flaggedCam = [];
    const match = (label, rules, category) => {
      const low = label.toLowerCase();
      for (const r of rules || []) {
        if (r.match.some((m) => low.includes(m))) {
          flagged.push({ category, name: r.name, severity: r.severity, evidence: `device=${label}` });
          return true;
        }
      }
      return false;
    };
    audio.forEach((l) => match(l, sig?.virtual_audio, "virtual_audio") && flaggedAudio.push(l));
    camera.forEach((l) => match(l, sig?.virtual_camera, "virtual_camera") && flaggedCam.push(l));

    setDevices({ audio, camera, flagged, denied });
    try {
      await api.post("/scan/precheck", {
        token,
        os_name: os,
        browser: detectBrowser(),
        audio_devices: flaggedAudio,
        camera_devices: flaggedCam,
        detections: flagged,
      });
    } catch {
      /* token may have been consumed meanwhile */
    }
    setChecking(false);
  };

  const downloadUrl = `${API}/agent/download?token=${token}`;
  const oneClick =
    os === "Windows"
      ? `irm "${API}/agent/install.ps1?token=${token}" | iex`
      : `curl -fsSL "${API}/agent/install.sh?token=${token}" | bash`;
  const [copiedCmd, setCopiedCmd] = useState(false);
  const copyCmd = () => {
    navigator.clipboard.writeText(oneClick);
    setCopiedCmd(true);
    setTimeout(() => setCopiedCmd(false), 1500);
  };

  const Back = () => (
    <button
      onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))}
      data-testid="candidate-back-button"
      className="flex items-center gap-2 text-sm text-slate-400 hover:text-slate-200"
    >
      <ArrowLeft className="w-4 h-4" /> Back
    </button>
  );

  return (
    <div className="min-h-screen soc-grid bg-[#070B14] flex items-start justify-center p-4 py-10">
      <div className="w-full max-w-3xl fade-up">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <ShieldAlert className="w-7 h-7 text-cyan-400" />
            <div>
              <div className="font-head font-bold text-lg leading-none">ANTI CHEATING TOOL</div>
              <div className="eyebrow mt-1">CANDIDATE PRE-INTERVIEW SYSTEM CHECK</div>
            </div>
          </div>
          <Back />
        </div>

        {status === "loading" && (
          <div className="card-soc p-8 flex items-center gap-3 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin text-cyan-400" /> Validating your one-time scan token…
          </div>
        )}

        {(status === "invalid" || status === "expired") && (
          <div className="card-soc p-8" data-testid="candidate-error">
            <div className="flex items-center gap-3 text-red-400 mb-2">
              <XCircle className="w-6 h-6" />
              <span className="font-head font-bold text-lg">
                {status === "expired" ? "This scan link has expired" : "Invalid scan link"}
              </span>
            </div>
            <p className="text-sm text-slate-400">
              {status === "expired"
                ? "The validity window set by the hiring team has passed. Please ask your recruiter for a new link."
                : "This link is not recognised. Please check the link you received or ask your recruiter for a new one."}
            </p>
          </div>
        )}

        {status === "completed" && result && (
          <div className="card-soc p-6" data-testid="candidate-result">
            <div className="flex items-center gap-2 text-emerald-400 mb-4">
              <CheckCircle2 className="w-5 h-5" />
              <span className="font-head font-bold text-lg">Scan complete — report delivered to the hiring team</span>
            </div>
            <div className="flex items-center gap-6 flex-wrap">
              <RiskGauge score={result.risk_score} verdict={result.verdict} size={150} />
              <div className="flex-1 min-w-[240px]">
                <div className="flex items-center gap-3 mb-2">
                  <VerdictBadge verdict={result.verdict} testid="candidate-verdict" />
                  {result.is_rescan && (
                    <span className={`text-xs font-mono ${result.environment_changed ? "text-red-400" : "text-emerald-400"}`}>
                      {result.environment_changed ? "environment changed vs baseline" : "consistent with baseline"}
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-400">
                  {result.detection_count === 0
                    ? "No AI cheating tools, hidden overlays or virtual devices were found on this machine."
                    : `${result.detection_count} item${result.detection_count === 1 ? "" : "s"} flagged. The interviewer can see full details.`}
                </p>
                {result.self_destruct && (
                  <p className="text-xs text-slate-500 mt-2 flex items-center gap-1.5">
                    <Trash2 className="w-3.5 h-3.5" /> The scanner removed itself from your computer. This link is now used.
                  </p>
                )}
              </div>
            </div>
            {result.detections?.length > 0 && (
              <div className="mt-5 divide-y divide-white/5 border-t border-white/8">
                {result.detections.map((d, i) => (
                  <div key={i} className="flex items-center justify-between py-2.5">
                    <div className="min-w-0">
                      <div className="text-sm text-slate-100">{d.name}</div>
                      <div className="text-xs text-slate-500 font-mono truncate">{d.evidence}</div>
                    </div>
                    <SeverityBadge severity={d.severity} />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {status === "pending" && (
          <div className="space-y-4">
            <div className="card-soc p-5 flex items-center justify-between">
              <div>
                <div className="eyebrow mb-1">CANDIDATE</div>
                <div className="font-head font-bold text-lg">{info?.candidate_name}</div>
              </div>
              <div className="text-right">
                <div className="eyebrow mb-1">YOUR SYSTEM</div>
                <div className="text-sm text-slate-300 flex items-center gap-1.5 justify-end">
                  <Monitor className="w-4 h-4 text-slate-500" /> {os} · {detectBrowser()}
                </div>
              </div>
            </div>

            {/* Step 1: real browser-level device check */}
            <div className="card-soc p-6" data-testid="device-check-card">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-head font-bold">Step 1 · Microphone & camera check</h3>
                <span className="eyebrow">RUNS IN YOUR BROWSER</span>
              </div>
              <p className="text-sm text-slate-400 mb-4">
                Checks your real audio and video devices for virtual microphones / cameras (VB-CABLE, VoiceMeeter,
                BlackHole, OBS Virtual Camera…). Your browser will ask for permission — nothing is recorded.
              </p>
              {!devices ? (
                <button
                  onClick={runDeviceCheck}
                  disabled={checking}
                  data-testid="run-device-check-button"
                  className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-[#04121a] font-semibold rounded-lg px-4 py-2.5 disabled:opacity-50"
                >
                  {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mic className="w-4 h-4" />}
                  {checking ? "Checking devices…" : "Run device check"}
                </button>
              ) : (
                <div className="space-y-3" data-testid="device-check-result">
                  {devices.denied && (
                    <div className="text-xs text-amber-400 bg-amber-500/10 rounded-lg px-3 py-2">
                      Permission was not granted, so device names are hidden by the browser. The deep scan below still
                      checks devices at system level.
                    </div>
                  )}
                  <div className="grid sm:grid-cols-2 gap-3">
                    <DeviceList icon={Mic} title="Audio devices" items={devices.audio} />
                    <DeviceList icon={Video} title="Cameras" items={devices.camera} />
                  </div>
                  {devices.flagged.length === 0 ? (
                    <div className="flex items-center gap-2 text-sm text-emerald-400">
                      <CheckCircle2 className="w-4 h-4" /> No virtual audio or camera devices detected.
                    </div>
                  ) : (
                    devices.flagged.map((f, i) => (
                      <div key={i} className="flex items-center justify-between text-sm bg-red-500/10 rounded-lg px-3 py-2">
                        <span className="text-red-300">{f.name} <span className="text-slate-500 font-mono text-xs">{f.evidence}</span></span>
                        <SeverityBadge severity={f.severity} />
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Step 2: real deep scan via the agent */}
            <div className="card-soc p-6" data-testid="deep-scan-card">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-head font-bold">Step 2 · Deep system scan</h3>
                <span className="eyebrow">RUNS ON YOUR COMPUTER</span>
              </div>
              <p className="text-sm text-slate-400 mb-4">
                Running apps, hidden overlays and background tools can't be seen from a web page. Run the one-time
                scanner — it checks your system offline, uploads the result here, then deletes itself.
              </p>
              <div className="eyebrow mb-1.5">
                {os === "Windows" ? "ONE-CLICK (POWERSHELL · NO PYTHON NEEDED)" : "ONE-CLICK (TERMINAL)"}
              </div>
              <div className="flex items-center gap-2 mb-2">
                <code className="term flex-1 px-3 py-2.5 text-xs text-cyan-300 overflow-x-auto whitespace-nowrap" data-testid="one-click-command">
                  {oneClick}
                </code>
                <button
                  onClick={copyCmd}
                  data-testid="copy-one-click-button"
                  className="p-2.5 rounded-lg bg-cyan-500/15 hover:bg-cyan-500/25 text-cyan-300 shrink-0"
                >
                  {copiedCmd ? <CheckCircle2 className="w-4 h-4" /> : <Terminal className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-slate-500 mb-4">
                {os === "Windows"
                  ? "Press Start, type PowerShell, open it, paste the command, press Enter. If Python isn't installed it downloads a portable runtime automatically (no admin rights needed). If HR sent you a sentinel_scanner.exe, just double-click it instead."
                  : "Open Terminal, paste the command, press Enter. Uses the built-in python3. If HR sent you a sentinel_scanner .app, just double-click it instead."}
              </p>
              <div className="flex items-center gap-2 text-sm text-slate-400 border-t border-white/8 pt-4" data-testid="waiting-for-result">
                <RefreshCw className="w-4 h-4 animate-spin text-cyan-400" />
                Waiting for the scanner result… this page refreshes automatically.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DeviceList({ icon: Icon, title, items }) {
  return (
    <div className="bg-[#0B1220] border border-white/8 rounded-lg p-3">
      <div className="eyebrow mb-2 flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5" /> {title} ({items.length})
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-slate-600">None visible</div>
      ) : (
        items.map((d, i) => (
          <div key={i} className="text-xs font-mono text-slate-400 truncate">{d}</div>
        ))
      )}
    </div>
  );
}
